import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { TextOneLine } from '@actual-app/components/text-one-line';
import { View } from '@actual-app/components/view';
import type { CategoryEntity } from '@actual-app/core/types/models';
import { css } from '@emotion/css';

import { AyudaGavi } from '#components/mobile/ayuda/AyudaGavi';
import { OjoBuey } from '#components/mobile/ayuda/OjoBuey';
import { usePreguntasRevisar } from '#components/mobile/ayuda/usePreguntasRevisar';
import { useColoresCategorias } from '#components/mobile/informes/useColoresCategorias';
import { MobileBackButton } from '#components/mobile/MobileBackButton';
import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import { categoriaIngreso } from '#components/mobile/transactions/seleccionCategoria';
import { Boton } from '#components/mobile/ui/Boton';
import { EstadoVacio } from '#components/mobile/ui/EstadoVacio';
import { Icono } from '#components/mobile/ui/Icono';
import { ImporteMovimiento } from '#components/mobile/ui/ImporteMovimiento';
import { PildoraCategoria } from '#components/mobile/ui/PildoraCategoria';
import { SeparadorFecha } from '#components/mobile/ui/SeparadorFecha';
import { zonaTactil } from '#components/mobile/ui/tactil';
import {
  color,
  densidad,
  espacio,
  num,
  radio,
  sombra,
  texto,
} from '#components/mobile/ui/tokens';
import { MobilePageHeader, Page } from '#components/Page';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useAccounts } from '#hooks/useAccounts';
import { useCategories } from '#hooks/useCategories';
import { useFormat } from '#hooks/useFormat';
import { useNavigate } from '#hooks/useNavigate';
import { usePayees } from '#hooks/usePayees';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { useUndo } from '#hooks/useUndo';
import { pushModal } from '#modals/modalsSlice';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';
import { registrarUso } from '#uso';

import {
  admiteChipIngreso,
  agruparPorFecha,
  alternar,
  alternarTodo,
  cambiosAprobar,
  esSinCategoria,
  listosParaAprobar,
  seleccionVigente,
  separarAprobables,
  sugerenciaDe,
  tipoTraspaso,
  todoSeleccionado,
  totalSeleccion,
} from './revisar';
import type { MovimientoPendiente } from './revisar';
import {
  aprobar,
  borrar,
  categorizar,
  usePorRevisar,
  useSugerencias,
} from './usePorRevisar';

/** Con la lista vacía, «Todo revisado ✓» y vuelta atrás. */
const VUELTA_MS = 1800;

/**
 * «Por revisar» (`/revisar`): los movimientos nuevos del banco o de un
 * archivo, por fecha, con casillas. Con selección, barra inferior «N
 * seleccionados · total» con Aprobar, Categorizar y la papelera (borra
 * directo con «Deshacer»). Tocar un movimiento abre su editor con «Aprobar»
 * flotante; tocar su categoría abre el selector y, al elegir, se aprueba
 * (H77/H78). Categorizar aquí siempre aprueba.
 */
export function RevisarPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pendientes: todos, cargando } = usePorRevisar();
  const { data: payees = [] } = usePayees();
  const { data: { list: categorias } = { list: [] as CategoryEntity[] } } =
    useCategories();
  const { data: cuentas = [] } = useAccounts();
  const fuera = useMemo(
    () => new Set(cuentas.filter(a => a.offbudget).map(a => a.id)),
    [cuentas],
  );
  // Lo que eliges aquí enseña a las reglas del comercio (Vadik, 19-oct-2026).
  const [aprenderPref = 'true'] = useSyncedPref('learn-categories');
  const aprender = String(aprenderPref) === 'true';
  // Todo lo nuevo se revisa junto, tenga categoría o no (Vadik, 26-oct-2026):
  // categorizar es parte de revisar, no otra lista.
  const pendientes = todos;
  // Categoría de ingreso para el chip «¿Nómina? ✓» de las entradas.
  const ingreso = useMemo(() => categoriaIngreso(categorias), [categorias]);
  const sugerencias = useSugerencias(pendientes);
  const { huecoDe } = useColoresCategorias();
  const [seleccionCruda, setSeleccion] = useState<Set<string>>(new Set());
  const { showUndoNotification } = useUndo();

  const ids = useMemo(() => pendientes.map(p => p.id), [pendientes]);
  const seleccion = useMemo(
    () => seleccionVigente(seleccionCruda, ids),
    [seleccionCruda, ids],
  );
  const grupos = useMemo(() => agruparPorFecha(pendientes), [pendientes]);
  const nombrePayee = useMemo(
    () => new Map(payees.map(p => [p.id, p.name])),
    [payees],
  );
  const nombreCategoria = useMemo(
    () => new Map<string, string>(categorias.map(c => [c.id, c.name])),
    [categorias],
  );
  // Cuenta del otro lado de un traspaso (el comercio «traspaso» la lleva).
  const destinoPayee = useMemo(
    () => new Map(payees.map(p => [p.id, p.transfer_acct ?? null])),
    [payees],
  );
  const nombreCuenta = useMemo(
    () => new Map(cuentas.map(a => [a.id, a.name])),
    [cuentas],
  );

  const vacia = !cargando && pendientes.length === 0;
  const [ayudaAbierta, setAyudaAbierta] = useState(false);
  const preguntasAyuda = usePreguntasRevisar();
  // La pantalla vacía se cierra sola: la ayuda se va con ella.
  if (vacia && ayudaAbierta) {
    setAyudaAbierta(false);
  }
  const volver = useCallback(() => {
    if (window.history.length > 1) {
      void navigate(-1);
    } else {
      void navigate('/inicio', { replace: true });
    }
  }, [navigate]);
  useEffect(() => {
    if (vacia) {
      const id = window.setTimeout(volver, VUELTA_MS);
      return () => window.clearTimeout(id);
    }
  }, [vacia, volver]);

  // Regla: sin categoría (en cuentas del presupuesto) no se aprueba.
  const { aprobables, bloqueados } = useMemo(
    () => separarAprobables(pendientes, seleccion, fuera),
    [pendientes, seleccion, fuera],
  );
  const avisar = (message: string) =>
    dispatch(addNotification({ notification: { type: 'warning', message } }));

  const onAprobar = async () => {
    if (aprobables.size === 0) {
      const unico = bloqueados.size === 1 ? [...bloqueados][0] : null;
      const m = unico ? pendientes.find(p => p.id === unico) : undefined;
      if (m && !m.is_parent) {
        // Uno solo sin categoría: aviso y selector; al elegir, se aprueba.
        avisar(t('Give it a category before approving it'));
        abrirSelectorYAprobar(
          m.id,
          t('Give it a category before approving it'),
        );
      } else if (m) {
        avisar(t('Give every part a category before approving it'));
      } else {
        avisar(t('Give them a category before approving them'));
      }
      return;
    }
    registrarUso('revisar', { accion: 'aprobar', n: aprobables.size });
    await aprobar(cambiosAprobar(pendientes, aprobables), aprender);
    if (bloqueados.size > 0) {
      // Los que no tienen categoría siguen seleccionados para categorizarlos.
      // También con «Deshacer», como la aprobación sin bloqueados.
      showUndoNotification({
        message: t(
          'Approved: {{approved}}. {{count}} have no category: give them one to approve them',
          { approved: aprobables.size, count: bloqueados.size },
        ),
      });
      setSeleccion(new Set(bloqueados));
    } else {
      showUndoNotification({
        message: t('{{count}} approved', { count: aprobables.size }),
      });
      setSeleccion(new Set());
    }
  };

  // Un movimiento: categoría + aprobado en un solo cambio, con «Deshacer».
  const categorizarUno = async (id: string, categoria: string) => {
    const hechos = await categorizar(
      pendientes,
      new Set([id]),
      categoria,
      aprender,
    );
    if (hechos > 0) {
      const nombre = nombreCategoria.get(categoria);
      showUndoNotification({
        message: nombre
          ? t('Approved as {{category}}', { category: nombre })
          : t('{{count}} approved', { count: 1 }),
      });
    }
  };
  const abrirSelectorYAprobar = (id: string, title: string) => {
    dispatch(
      pushModal({
        modal: {
          name: 'category-autocomplete',
          options: {
            // El aviso va en el título: el selector tapa la notificación.
            title,
            onSelect: categoria => {
              if (categoria) {
                registrarUso('revisar', {
                  accion: 'categorizar-y-aprobar',
                  n: 1,
                  categoria,
                });
                void categorizarUno(id, categoria);
                setSeleccion(s => {
                  const r = new Set(s);
                  r.delete(id);
                  return r;
                });
              }
            },
            permitirCrear: true,
          },
        },
      }),
    );
  };
  const onCategorizar = () => {
    dispatch(
      pushModal({
        modal: {
          name: 'category-autocomplete',
          options: {
            title: t('Categorize {{count}} transactions', {
              count: seleccion.size,
            }),
            onSelect: id => {
              if (id) {
                registrarUso('revisar', {
                  accion: 'categorizar',
                  n: seleccion.size,
                  categoria: id,
                });
                void categorizar(pendientes, seleccion, id, aprender).then(
                  hechos => {
                    if (hechos > 0) {
                      showUndoNotification({
                        message: t('{{count}} categorized and approved', {
                          count: hechos,
                        }),
                      });
                    }
                  },
                );
              }
            },
            permitirCrear: true,
          },
        },
      }),
    );
  };

  const onIngreso = (id: string) => {
    if (ingreso) {
      registrarUso('revisar', {
        accion: 'chip-ingreso',
        n: 1,
        categoria: ingreso.id,
      });
      void categorizarUno(id, ingreso.id);
    }
  };

  // Chip «¿Supermercat? ✓»: acepta la sugerida y aprueba.
  const onSugerencia = (id: string, categoria: string) => {
    registrarUso('revisar', { accion: 'chip-sugerencia', n: 1, categoria });
    void categorizarUno(id, categoria);
  };

  // «Aprobar los N que ya tienen categoría» (nunca los sin categoría).
  const listos = useMemo(
    () => listosParaAprobar(pendientes, fuera),
    [pendientes, fuera],
  );
  const onAprobarListos = async () => {
    const k = listos.size;
    registrarUso('revisar', { accion: 'aprobar-listos', n: k });
    await aprobar([...listos].map(id => ({ id, reviewed: true as const })));
    showUndoNotification({ message: t('{{count}} approved', { count: k }) });
  };

  // Papelera: borra directo; «Deshacer» los recupera.
  const onBorrar = async () => {
    const k = seleccion.size;
    registrarUso('revisar', { accion: 'borrar', n: k });
    await borrar(seleccion);
    setSeleccion(new Set());
    showUndoNotification({ message: t('{{count}} deleted', { count: k }) });
  };

  const n = seleccion.size;

  // Con la barra de selección abajo, los avisos suben por encima de ella
  // (si no, el texto del aviso se monta sobre Aprobar / Categorizar).
  // Los avisos («Deshacer») salen arriba en el móvil (H30): la barra de
  // selección ya no tiene que empujarlos.

  return (
    <Page
      header={
        <MobilePageHeader
          title={t('To review')}
          leftContent={<MobileBackButton />}
          rightContent={
            !vacia &&
            !cargando && <OjoBuey onPress={() => setAyudaAbierta(true)} />
          }
        />
      }
      padding={0}
    >
      {vacia ? (
        <View style={{ padding: espacio.margen }} data-testid="revisar-vacio">
          <EstadoVacio
            ilustracion="barquito"
            titulo={t('All reviewed ✓')}
            texto={t('No new transactions to approve.')}
            accion={
              // «Volver» ya está en la cabecera: aquí, ir a la pestaña Movimientos.
              <Boton
                onPress={() => void navigate('/gastos')}
                data-testid="revisar-ir-gastos"
              >
                <Trans>Go to Transactions</Trans>
              </Boton>
            }
          />
        </View>
      ) : (
        <View style={{ paddingBottom: MOBILE_NAV_HEIGHT + (n > 0 ? 110 : 24) }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: `8px ${espacio.margen}px`,
            }}
          >
            <Text
              data-ayuda="revisar-contador"
              style={{ ...texto.secundario, color: color.fg2 }}
            >
              {t('{{count}} new transactions', {
                count: pendientes.length,
              })}
            </Text>
            <Button
              variant="bare"
              data-testid="revisar-todo"
              onPress={() => setSeleccion(alternarTodo(seleccion, ids))}
              style={{
                minHeight: 36,
                padding: '0 4px',
                ...densidad.pequeno,
                fontWeight: 700,
                color: color.accent,
              }}
            >
              {todoSeleccionado(seleccion, ids)
                ? t('Clear selection')
                : t('Select all')}
            </Button>
          </View>
          {listos.size > 0 && (
            <View style={{ padding: `0 ${espacio.margen}px 10px` }}>
              <Boton
                variante="fantasma"
                data-testid="revisar-aprobar-listos"
                onPress={() => void onAprobarListos()}
                style={{ width: '100%' }}
              >
                <Icono nombre="check" size={18} />
                {t('Approve the {{count}} that already have a category', {
                  count: listos.size,
                })}
              </Boton>
            </View>
          )}
          {grupos.map(g => (
            <View key={g.fecha} style={{ flexShrink: 0 }}>
              <SeparadorFecha fecha={g.fecha} />
              {g.movimientos.map(m => {
                const sugeridaId = sugerenciaDe(m, sugerencias);
                const sugerida = sugeridaId
                  ? nombreCategoria.get(sugeridaId)
                  : undefined;
                const chip =
                  sugeridaId && sugerida
                    ? {
                        tipo: 'sugerencia' as const,
                        nombre: sugerida,
                        onPress: () => onSugerencia(m.id, sugeridaId),
                      }
                    : ingreso && admiteChipIngreso(m, fuera, sugeridaId)
                      ? {
                          tipo: 'ingreso' as const,
                          nombre: ingreso.name,
                          onPress: () => onIngreso(m.id),
                        }
                      : undefined;
                const traspaso = tipoTraspaso(
                  m,
                  fuera,
                  m.payee ? destinoPayee.get(m.payee) : null,
                );
                // La etiqueta abre el selector si la fila lleva categoría
                // (o le falta). Divididos y traspasos entre cuentas del
                // presupuesto, no; los que salen a una cuenta fuera, sí.
                const categoriaTocable =
                  !m.is_parent &&
                  (!m.transfer_id || traspaso === 'a-fuera') &&
                  (!!m.category ||
                    esSinCategoria(m, fuera) ||
                    traspaso === 'a-fuera');
                return (
                  <FilaPendiente
                    key={m.id}
                    m={m}
                    tipo={
                      m.is_parent
                        ? 'dividido'
                        : traspaso === 'dentro'
                          ? 'traspaso'
                          : traspaso === 'a-fuera'
                            ? 'traspaso-fuera'
                            : esSinCategoria(m, fuera)
                              ? 'sin-categoria'
                              : 'normal'
                    }
                    marcado={seleccion.has(m.id)}
                    payee={m.payee ? nombrePayee.get(m.payee) : undefined}
                    categoria={
                      m.category ? nombreCategoria.get(m.category) : undefined
                    }
                    hueco={m.category ? huecoDe(m.category) : null}
                    cuenta={nombreCuenta.get(m.account) ?? ''}
                    onMarcar={() => setSeleccion(alternar(seleccion, m.id))}
                    onAbrir={() =>
                      void navigate(`/transactions/${m.id}?revisar=1`)
                    }
                    onCategoria={
                      categoriaTocable
                        ? () => abrirSelectorYAprobar(m.id, t('Category'))
                        : undefined
                    }
                    chip={chip}
                  />
                );
              })}
            </View>
          ))}
        </View>
      )}

      {n > 0 && !vacia && (
        <View
          data-testid="revisar-barra"
          style={{
            position: 'fixed',
            left: 8,
            right: 8,
            // Encima de la barra de pestañas (fija).
            bottom: `calc(${MOBILE_NAV_HEIGHT - 10}px + env(safe-area-inset-bottom))`,
            zIndex: 50,
            gap: 10,
            padding: 12,
            borderRadius: radio.heroe,
            backgroundColor: color.surface,
            boxShadow: sombra.hoja,
          }}
        >
          <PrivacyFilter>
            <Text
              style={{ ...num, fontSize: 14, fontWeight: 700, color: color.fg }}
              data-testid="revisar-resumen"
            >
              {t('{{count}} selected · {{amount}}', {
                count: n,
                amount: format(
                  totalSeleccion(pendientes, seleccion),
                  'financial',
                ),
              })}
            </Text>
          </PrivacyFilter>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Boton
              data-testid="revisar-aprobar"
              data-bloqueado={aprobables.size === 0 || undefined}
              onPress={() => void onAprobar()}
              // Apagado (pero se puede tocar para ver el aviso) si ninguno
              // de los seleccionados se puede aprobar.
              style={{ flex: 1, opacity: aprobables.size === 0 ? 0.45 : 1 }}
            >
              <Icono nombre="check" size={18} />
              <Trans>Approve</Trans>
            </Boton>
            <Boton
              variante="tonal"
              data-testid="revisar-categorizar"
              onPress={onCategorizar}
              style={{ flex: 1 }}
            >
              <Icono nombre="tag" size={18} />
              <Trans>Categorize</Trans>
            </Boton>
            <Boton
              variante="tonal"
              aria-label={t('Delete')}
              data-testid="revisar-borrar"
              onPress={() => void onBorrar()}
              style={{ padding: '0 12px' }}
            >
              <Icono nombre="trash" size={18} />
            </Boton>
          </View>
        </View>
      )}
      {ayudaAbierta && (
        <AyudaGavi
          pantalla={t('To review')}
          preguntas={preguntasAyuda}
          onCerrar={() => setAyudaAbierta(false)}
        />
      )}
    </Page>
  );
}

/** Para la ayuda: qué clase de fila es (Gavi busca una de cada). */
type TipoFila =
  | 'normal'
  | 'sin-categoria'
  | 'dividido'
  | 'traspaso'
  | 'traspaso-fuera';

function FilaPendiente({
  m,
  tipo,
  marcado,
  payee,
  categoria,
  hueco,
  cuenta,
  onMarcar,
  onAbrir,
  onCategoria,
  chip,
}: {
  m: MovimientoPendiente;
  tipo: TipoFila;
  marcado: boolean;
  payee: string | undefined;
  categoria: string | undefined;
  hueco: number | null;
  cuenta: string;
  onMarcar: () => void;
  onAbrir: () => void;
  /** Toque en la etiqueta de categoría: selector y, al elegir, aprobar. */
  onCategoria?: () => void;
  /** Chip de un toque «¿Supermercat? ✓»: categoriza y aprueba. */
  chip?: {
    tipo: 'sugerencia' | 'ingreso';
    nombre: string;
    onPress: () => void;
  };
}) {
  const { t } = useTranslation();
  const etiqueta = categoria ? (
    <PildoraCategoria nombre={categoria} hueco={hueco} tamano="pequena" />
  ) : m.is_parent ? (
    <Text style={{ ...densidad.pequeno, color: color.fg3 }}>
      <Trans>Split (several categories)</Trans>
    </Text>
  ) : tipo === 'traspaso' ? (
    // Entre cuentas del presupuesto: no le falta nada (no es gasto ni
    // ingreso), así que no sale el «Sin categoría» en ámbar.
    <Text style={{ ...densidad.pequeno, color: color.fg3 }}>
      <Trans>Transfer</Trans>
    </Text>
  ) : (
    <Text style={{ ...densidad.pequeno, color: color.warn }}>
      <Trans>Uncategorized</Trans>
    </Text>
  );
  return (
    <View
      data-testid="revisar-fila"
      data-id={m.id}
      data-ayuda-fila={tipo}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: marcado ? color.accentSoft : color.surface,
        boxShadow: `inset 0 -1px 0 ${color.line}`,
      }}
    >
      <Button
        variant="bare"
        aria-pressed={marcado}
        aria-label={t('Select {{payee}}', { payee: payee ?? '' })}
        data-testid="revisar-casilla"
        onPress={onMarcar}
        style={{
          width: 48,
          minHeight: 60,
          padding: 0,
          borderRadius: 0,
          flexShrink: 0,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 22,
            height: 22,
            borderRadius: 7,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: marcado ? color.accent : 'transparent',
            border: marcado ? 0 : `2px solid ${color.line2}`,
            color: color.accentInk,
          }}
        >
          {marcado && <Icono nombre="check" size={14} />}
        </span>
      </Button>
      <View
        style={{
          flex: 1,
          minWidth: 0,
          alignSelf: 'stretch',
          position: 'relative',
        }}
      >
        {/* Toda la fila abre el editor; la etiqueta de categoría va encima
            con su propio toque (sin botones anidados). */}
        <Button
          variant="bare"
          data-testid="revisar-abrir"
          aria-label={payee || t('(No payee)')}
          onPress={onAbrir}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            padding: 0,
            borderRadius: 0,
          }}
        />
        <View
          style={{
            position: 'relative',
            pointerEvents: 'none',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            minHeight: 60,
            padding: `8px ${chip ? 8 : espacio.margen}px 8px 0`,
            color: color.fg,
          }}
        >
          <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
            <TextOneLine
              data-ayuda="comercio"
              style={{ fontSize: 15, fontWeight: 700 }}
            >
              {payee || t('(No payee)')}
            </TextOneLine>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                minWidth: 0,
              }}
            >
              {onCategoria ? (
                <Button
                  variant="bare"
                  data-testid="revisar-categoria"
                  aria-label={t('Change category of {{payee}}', {
                    payee: payee ?? '',
                  })}
                  onPress={onCategoria}
                  className={css(zonaTactil())}
                  style={{
                    pointerEvents: 'auto',
                    padding: 0,
                    minHeight: 22,
                    minWidth: 0,
                    flexShrink: 1,
                    borderRadius: radio.pildora,
                    overflow: 'visible',
                    justifyContent: 'flex-start',
                  }}
                >
                  {etiqueta}
                </Button>
              ) : (
                <span data-ayuda="categoria">{etiqueta}</span>
              )}
              <TextOneLine style={{ ...densidad.pequeno, color: color.fg3 }}>
                {cuenta}
              </TextOneLine>
            </View>
          </View>
          <span data-ayuda="importe">
            <ImporteMovimiento valor={m.amount} />
          </span>
        </View>
      </View>
      {chip && (
        // Un toque: pone la categoría y aprueba (con «Deshacer»).
        <Button
          variant="bare"
          data-testid="revisar-chip"
          data-ayuda-chip={chip.tipo}
          aria-label={t('Categorize as {{name}} and approve', {
            name: chip.nombre,
          })}
          onPress={chip.onPress}
          style={{
            minHeight: 44,
            padding: `0 ${espacio.margen}px 0 4px`,
            borderRadius: 0,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              ...densidad.pequeno,
              display: 'inline-block',
              maxWidth: 150,
              lineHeight: '30px',
              padding: '0 10px',
              borderRadius: radio.pildora,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: color.ok,
              backgroundColor: color.okSoft,
            }}
          >
            {t('{{name}}? ✓', { name: chip.nombre })}
          </span>
        </Button>
      )}
    </View>
  );
}
